import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import 'chartjs-adapter-luxon';
import zoomPlugin from 'chartjs-plugin-zoom';
import { PanelModule } from 'primeng/panel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ChartModule, UIChart } from '../../primeng/chart/chart.component';
import { Graph } from '../../models/graph';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs/internal/Subscription';
import { Observable } from 'rxjs/internal/Observable';
import { Constants } from '../../utilities/constants';
import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';

@Component({
  selector: 'app-trend',
  imports: [
    CommonModule,
    FormsModule,
    PanelModule,
    ProgressSpinnerModule,
    ChartModule,
    ButtonModule,
    SelectButtonModule,
    TranslatePipe
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './trend.component.html',
  styleUrl: './trend.component.css'
})
export class TrendComponent implements OnInit {
  private zoomCommandSubscription: Subscription | undefined;
  private zoomLevelSubscription: Subscription | undefined;

  private _xMin: number = 0;
  private _xMax: number = 0;


  @Input() icona: string = 'pi pi-blank';
  @Input() titolo: string = 'primeng.nullLabel';
  @Input() loading: boolean = false;
  
  private _segnale: Graph[][] = [];
  @Input() set segnale(value: Graph[][]) {
    this._segnale = value;

    this.data.datasets = [];
    this.initialZoom = [];
    var i: number = 0;
    this._segnale.forEach(v => {
      if (v.length > 0) {
        this.data.datasets.push({
          data: v,
          fill: false,
          parsing: false,
          borderColor: this.colors[i],       // Applicato dinamicamente dal CSS
          backgroundColor: this.colors[i],   // Puoi applicare opacità se la variabile è in formato RGBA o lasciarlo pieno
          pointBackgroundColor: this.colors[i]
        })
      }
      i = i + 1;
    })
  }

  get segnale(): Graph[][] {
    return this._segnale;
  }

  @Input() set minX(value: number) {
    this.options.scales.x.min = value;
    this._xMin = value;
  }
  get minX(): number {
    return this._xMin;
  }

  @Input() set maxX(value: number) {
    const now = new Date();
    const midnightUtc = Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
      now.getUTCDate()
    );
    if (value > midnightUtc) {
      this.options.scales.x.time.unit = 'hour';
      this.options.scales.x.time.tooltipFormat = 'HH:mm';
    } else {
      this.options.scales.x.time.unit = 'day';
      this.options.scales.x.time.tooltipFormat = 'dd LLL';
    }

    this.options.scales.x.max = value;
    this._xMax = value;
  }

  get maxX(): number {
    return this._xMax;
  }

  private colors: string[] = ['#00FF00', '#00FF00']
  @Input() set colorRest(value: string) {
    this.colors[0] = this.getCssVariable(value);
  }

  get colorRest(): string {
    return this.colors[0];
  }

  @Input() set colorMovement(value: string) {
    this.colors[1] = this.getCssVariable(value);
  }

  get colorMovement(): string {
    return this.colors[1];
  }

  @ViewChild('chart') myChartRef: UIChart | undefined;

  zoomFromInput: boolean = false;
  @Output() onZoomChanged = new EventEmitter<any>();
  @Input() zoomLevel: Observable<any> | undefined;

  zoomToOutput: boolean = false;
  @Output() onZoomCommand = new EventEmitter<number>();
  @Input() zoomCommand: Observable<number> | undefined;

  data: any;
  options: any;
  plugins: any[] = [];

  initialZoom: {id: string, value: number}[] = [];

  modeOptions:  any[] = [
    { icon: 'pi pi-search', value: 1 },
    { icon: 'pi pi-arrows-alt', value: 2 }
  ];
  mode: number = 2;

  constructor(private translate: TranslateService) {
    this.plugins = [zoomPlugin];
    const currentLang = this.translate.currentLang() || 'en';

    this.data = {
      datasets: []
    }

    this.options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          offset: true,
          type: 'time',
          time: {
            unit: 'day',
            tooltipFormat: 'dd LLL',
            displayFormats: {
              day: 'dd LLL',
              hour: 'HH:mm'
            }
          },
          ticks: {
            autoSkip: false,
            maxRotation: 45,
            minRotation: 45,
            source: 'auto'
          },
          adapters: {
            date: {
              zone: 'utc',
              locale: currentLang
            }
          }
        },
        y: {
          offset: true,
          type: 'linear',
          title: {
            display: true,
            text: ''
          },
          ticks: {
            precision: 0 
          }
        }
      },
      plugins: {
        legend: {
          display: false,
        },
        zoom: {
          pan: {
            enabled: false,
            mode: 'xy',
          },
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            drag: {
              enabled: true,
              borderWidth: 4
            },
            mode: 'x',
            scaleMode: 'xy',
            onZoomComplete: (mychart: any) => {
              if (this.zoomFromInput) {
                this.zoomFromInput = false;
              } else {
                const scaleBounds = mychart.chart.getZoomedScaleBounds();
                const scaleX: { min: number | undefined, max: number | undefined } = scaleBounds['x'];
                if (scaleX.min === undefined || scaleX.max === undefined) return;

                this.zoomToOutput = true;
                this.onZoomChanged.emit(scaleX)
              }
            }
          }
        }
      }
    };
  }

    ngOnInit(): void {
    this.zoomCommandSubscription = this.zoomCommand?.subscribe((event: number) => {
      if (this.zoomToOutput) {
        this.zoomToOutput = false;
        return;
      }

      this.zoomFromInput = true;
      switch (event) {
        case Constants.ZOOM_IN:
          this.onZoomIn();
          break;

        case Constants.ZOOM_OUT:
          this.onZoomOut();
          break;

        case Constants.ZOOM_RST:
          this.onZoomReset();
          break;

        default:
          this.zoomFromInput = false;
          break;
      }
    });

    this.zoomLevelSubscription = this.zoomLevel?.subscribe((scaleX: any) => {
      if (this.zoomToOutput) {
        this.zoomToOutput = false;
        return;
      }

      this.zoomFromInput = true;
      this.myChartRef?.chart.zoomScale('x', scaleX);
    });
  }

  ngOnDestroy(): void {
    this.zoomCommandSubscription?.unsubscribe();
    this.zoomLevelSubscription?.unsubscribe();
  }

  onModeSelection(): void {
    const useZoom: boolean = this.mode === 1;
    const usePan: boolean = this.mode === 2;

    this.options.plugins.zoom.pan.enabled = usePan;
    this.options.plugins.zoom.zoom.wheel.enabled = useZoom;
    this.options.plugins.zoom.zoom.pinch.enabled = useZoom;
    this.options.plugins.zoom.zoom.drag.enabled = useZoom;

    this.myChartRef?.chart.update();
  }

  onZoomIn() {
    if (this.zoomFromInput) {
      this.zoomFromInput = false;
    } else {
      this.zoomToOutput = true;
      this.onZoomCommand.emit(Constants.ZOOM_IN);
    }

    this.myChartRef?.chart.zoom(1.2);
  }

  onZoomOut() {
    if (this.zoomFromInput) {
      this.zoomFromInput = false;
    } else {
      this.zoomToOutput = true;
      this.onZoomCommand.emit(Constants.ZOOM_OUT);
    }

    this.myChartRef?.chart.zoom(0.8);
  }

  onZoomReset() {
    if (this.zoomFromInput) {
      this.zoomFromInput = false;
    } else {
      this.zoomToOutput = true;
      this.onZoomCommand.emit(Constants.ZOOM_RST);
    }

    this.myChartRef?.chart.resetZoom();
  }

  private getCssVariable(variableName: string): string {
    const globalStyles = window.getComputedStyle(document.documentElement);
    const color = globalStyles.getPropertyValue(variableName).trim();
  
    return color || '#000000'; 
  }
}
